/**
 * Predictive Search Component (Desktop)
 * 
 * Features:
 * - Focus State: Trending keywords, color filters, most searched products
 * - Keyword Entered State: Products, collections, pages with "View all results"
 * - Caching for performance
 * - AbortController for request cancellation
 * - Debounced input handling
 */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    // State
    this.isOpen = false;
    this.currentQuery = '';
    this.abortController = null;
    this.cache = new Map();
    this.cacheMaxSize = 50;

    // DOM Elements
    this.input = this.querySelector('[data-predictive-search-input]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.closeButton = this.querySelector('[data-search-close]');
    this.resultsContainer = this.querySelector('#predictive-search');
    this.focusState = this.querySelector('[data-focus-state]');
    this.keywordState = this.querySelector('[data-keyword-state]');
    this.noResultsState = this.querySelector('[data-no-results-state]');
    this.loadingState = this.querySelector('[data-loading-state]');
    this.viewAllSection = this.querySelector('[data-view-all-section]');
    this.viewAllLink = this.querySelector('[data-view-all-link]');
    this.viewAllKeyword = this.querySelector('[data-view-all-keyword]');
    this.productsGrid = this.querySelector('[data-products-grid]');
    this.collectionsList = this.querySelector('[data-collections-list]');
    this.collectionsSection = this.querySelector('[data-collections-section]');
    this.pagesList = this.querySelector('[data-pages-list]');
    this.pagesSection = this.querySelector('[data-pages-section]');

    // External elements
    this.searchToggle = document.querySelector('#search-toggle');
    this.overlay = document.getElementById('overlay');
    this.navDrawer = document.querySelector('nav-drawer');
    this.header = document.querySelector('.nav__root');

    // Bind methods
    this.handleInput = this.debounce(this.handleInput.bind(this), 300);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleSuggestionClick = this.handleSuggestionClick.bind(this);
    this.handleColorClick = this.handleColorClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
  }

  connectedCallback() {
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  setupEventListeners() {
    if (this.input) {
      this.input.addEventListener('input', this.handleInput);
      this.input.addEventListener('focus', this.handleFocus);
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.handleClear);
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.handleClose);
    }

    // Suggestion tag clicks
    this.querySelectorAll('[data-suggestion]').forEach(tag => {
      tag.addEventListener('click', this.handleSuggestionClick);
    });

    // Color filter clicks
    this.querySelectorAll('[data-color-filter]').forEach(swatch => {
      swatch.addEventListener('click', this.handleColorClick);
    });

    // Keyboard navigation
    document.addEventListener('keydown', this.handleKeydown);

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', this.handleOverlayClick);
    }

    // Stop click propagation on search panel so clicks on it don't close search
    this.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }

  removeEventListeners() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  // ==================
  // State Management
  // ==================

  reveal() {
    if (this.isOpen) return;

    // Store current scroll position to prevent unwanted scrolling
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;

    // Close cart if open
    try {
      window.closeCart?.();
    } catch {}

    // Collapse mobile menu if open
    if (this.navDrawer?.mobileCollapse) {
      this.navDrawer.mobileCollapse();
    }

    this.isOpen = true;
    this.setAttribute('data-search-state', 'open');
    
    // Update toggle state
    if (this.searchToggle) {
      this.searchToggle.setAttribute('aria-expanded', 'true');
    }

    // Show overlay
    if (this.overlay) {
      this.overlay.classList.add('active', 'search-overlay');
      // Position overlay to match search panel (at nav__root top)
      // Overlay should start from the same position as the search bar
      const setOverlayPosition = () => {
        if (this.header) {
          const headerRect = this.header.getBoundingClientRect();
          const isTransparent = this.header.dataset.transparent === 'true';
          const isScrolled = this.header.classList.contains('nav__scrolled');
          const currentScrollY = window.scrollY || window.pageYOffset;
          
          let overlayTop;
          
          if (isTransparent && !isScrolled && currentScrollY === 0) {
            // When at top of page in transparent mode, header is absolutely positioned
            // Use 0 to align with top of viewport (where header visually appears)
            overlayTop = 0;
          } else {
            // When scrolled or not transparent, use actual header position
            overlayTop = headerRect.top;
          }
          
          // Use setProperty with important to override CSS rules
          // Overlay should cover entire viewport from this position
          this.overlay.style.setProperty('top', `${overlayTop}px`, 'important');
        }
      };
      // Set position immediately and again after a short delay to catch any layout shifts
      setOverlayPosition();
      setTimeout(setOverlayPosition, 50);
      setTimeout(setOverlayPosition, 150);
    }

    // Add class to header for nav hiding
    if (this.header) {
      this.header.classList.add('nav__search-active');
    }

    // Lock body scroll
    lockBodyScroll();

    // Restore scroll position immediately to prevent any scroll jumps
    window.scrollTo(scrollX, scrollY);

    // Position search at nav__root top to align perfectly
    const setSearchPosition = () => {
      if (this.header) {
        const headerRect = this.header.getBoundingClientRect();
        const isTransparent = this.header.dataset.transparent === 'true';
        const isScrolled = this.header.classList.contains('nav__scrolled');
        const currentScrollY = window.scrollY || window.pageYOffset;
        
        let headerTop;
        
        if (isTransparent && !isScrolled && currentScrollY === 0) {
          // When at top of page in transparent mode, header is absolutely positioned
          // Use 0 to align with top of viewport (where header visually appears)
          headerTop = 0;
        } else {
          // When scrolled or not transparent, use actual header position
          headerTop = headerRect.top;
        }
        
        this.style.setProperty('top', `${headerTop}px`, 'important');
        this.style.setProperty('max-height', `calc(100vh - ${headerTop}px)`, 'important');
      }
      
      // Restore scroll position after positioning to prevent any scroll jumps
      window.scrollTo(scrollX, scrollY);
    };
    // Set position immediately and again after a short delay to catch any layout shifts
    setSearchPosition();
    setTimeout(setSearchPosition, 50);
    setTimeout(setSearchPosition, 150);

    // Focus input without scrolling
    setTimeout(() => {
      if (this.input) {
        // Use preventScroll option if supported, otherwise restore scroll after focus
        if (this.input.focus && typeof this.input.focus === 'function') {
          try {
            this.input.focus({ preventScroll: true });
          } catch (e) {
            // Fallback for browsers that don't support preventScroll
            this.input.focus();
            // Restore scroll position after focus
            requestAnimationFrame(() => {
              window.scrollTo(scrollX, scrollY);
            });
          }
        }
      }
    }, 100);

    // Show focus state if no query
    if (!this.input?.value.trim()) {
      this.showFocusState();
    }

    // Trap focus
    if (typeof trapFocus === 'function') {
      trapFocus(this);
    }
  }

  hide() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.setAttribute('data-search-state', 'closed');

    // Update toggle state
    if (this.searchToggle) {
      this.searchToggle.setAttribute('aria-expanded', 'false');
    }

    // Hide overlay with fade animation
    if (this.overlay) {
      // Add fading class to trigger CSS fade while keeping position
      this.overlay.classList.remove('active');
      this.overlay.classList.add('overlay-fading');
      setTimeout(() => {
        // Now remove all search-related classes and inline styles
        this.overlay.classList.remove('search-overlay', 'overlay-fading');
        this.overlay.style.removeProperty('top');
      }, 300); // Match the overlay transition duration
    }

    // Remove class from header
    if (this.header) {
      this.header.classList.remove('nav__search-active');
    }

    // Reset inline styles
    this.style.top = '';
    this.style.maxHeight = '';

    // Unlock body scroll
    unlockBodyScroll();

    // Cancel any pending requests
    this.cancelPendingRequests();

    // Hide all states
    this.hideAllStates();

    // Remove focus trap
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.searchToggle);
    }
  }

  showFocusState() {
    this.hideAllStates();
    if (this.focusState) {
      this.focusState.hidden = false;
    }
  }

  showKeywordState() {
    this.hideAllStates();
    if (this.keywordState) {
      this.keywordState.hidden = false;
    }
  }

  showNoResultsState() {
    this.hideAllStates();
    if (this.noResultsState) {
      this.noResultsState.hidden = false;
    }
  }

  showLoadingState() {
    if (this.loadingState) {
      this.loadingState.hidden = false;
    }
  }

  hideLoadingState() {
    if (this.loadingState) {
      this.loadingState.hidden = true;
    }
  }

  hideAllStates() {
    if (this.focusState) this.focusState.hidden = true;
    if (this.keywordState) this.keywordState.hidden = true;
    if (this.noResultsState) this.noResultsState.hidden = true;
    if (this.loadingState) this.loadingState.hidden = true;
  }

  // ==================
  // Event Handlers
  // ==================

  handleInput() {
    const query = this.input?.value.trim() || '';
    
    // Update clear button visibility
    if (this.clearButton) {
      this.clearButton.hidden = query.length === 0;
    }

    if (!query) {
      this.currentQuery = '';
      this.cancelPendingRequests();
      this.showFocusState();
      return;
    }

    this.currentQuery = query;
    this.getSearchResults(query);
  }

  handleFocus() {
    if (!this.isOpen) {
      this.reveal();
    } else if (!this.input?.value.trim()) {
      this.showFocusState();
    }
  }

  handleClear() {
    if (this.input) {
      this.input.value = '';
      // Focus without scrolling
      try {
        this.input.focus({ preventScroll: true });
      } catch (e) {
        // Fallback for browsers that don't support preventScroll
        this.input.focus();
      }
    }
    if (this.clearButton) {
      this.clearButton.hidden = true;
    }
    this.currentQuery = '';
    this.cancelPendingRequests();
    this.showFocusState();
  }

  handleClose() {
    this.hide();
    if (this.input) {
      this.input.value = '';
    }
    if (this.clearButton) {
      this.clearButton.hidden = true;
    }
    this.currentQuery = '';
  }

  handleSuggestionClick(event) {
    event.preventDefault();
    const tag = event.currentTarget;
    const query = tag.textContent.trim();
    
    if (this.input) {
      this.input.value = query;
    }
    
    this.currentQuery = query;
    this.getSearchResults(query);
  }

  handleColorClick(event) {
    event.preventDefault();
    const swatch = event.currentTarget;
    const color = swatch.dataset.colorFilter;
    
    if (color) {
      // Navigate to search with color filter
      window.location.href = `/search?q=${encodeURIComponent(color)}&type=product`;
    }
  }

  handleKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.hide();
    }
  }

  handleOverlayClick(event) {
    // Close search when clicking on overlay (but not on search panel content)
    if (this.isOpen && event.target === this.overlay) {
      this.hide();
    }
  }

  // ==================
  // Search API
  // ==================

  async getSearchResults(query) {
    // Check cache first
    const cached = this.getFromCache(query);
    if (cached) {
      this.renderResults(cached, query);
      return;
    }

    // Cancel any pending requests
    this.cancelPendingRequests();

    // Create new abort controller
    this.abortController = new AbortController();

    // Show loading state
    this.showLoadingState();

    try {
      const response = await fetch(
        `/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=8&section_id=predictive-search`,
        { signal: this.abortController.signal }
      );

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const html = await response.text();
      const doc = parseHTML(html);
      const results = doc.querySelector('#shopify-section-predictive-search');

      if (results) {
        // Parse results
        const data = this.parseResults(results);
        
        // Cache results
        this.addToCache(query, data);
        
        // Render if this is still the current query
        if (query === this.currentQuery) {
          this.renderResults(data, query);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was cancelled, ignore
        return;
      }
      console.error('Search error:', error);
      this.hideLoadingState();
      this.showFocusState();
    }
  }

  parseResults(container) {
    const data = {
      products: [],
      collections: [],
      pages: [],
      terms: ''
    };

    // Get search terms
    const termsInput = container.querySelector('[data-search-terms]');
    if (termsInput) {
      data.terms = termsInput.value;
    }

    // Parse products (using card-product structure)
    container.querySelectorAll('[data-product-result]').forEach(el => {
      const img = el.querySelector('img');
      const titleLink = el.querySelector('.card__heading a');
      const productLink = el.querySelector('.card-product__media');
      const priceContainer = el.querySelector('.price');
      const priceRegular = el.querySelector('.price-item--regular');
      const priceSale = el.querySelector('.price-item--sale');
      const comparePriceEl = el.querySelector('.price__sale s.price-item--regular');
      
      const url = productLink?.href || titleLink?.href || '';
      const title = titleLink?.textContent?.trim() || '';
      const variantLabel = el.querySelector('.card__variant')?.textContent?.trim() || '';
      const price = priceSale?.textContent?.trim() || priceRegular?.textContent?.trim() || '';
      const comparePrice = comparePriceEl?.textContent?.trim() || '';
      const onSale = priceContainer?.classList.contains('price--on-sale');
      
      data.products.push({
        url,
        title,
        variantLabel,
        image: img ? {
          src: img.src,
          srcset: img.srcset,
          alt: img.alt
        } : null,
        comparePrice,
        price,
        onSale
      });
    });

    // Parse collections
    container.querySelectorAll('[data-collection-result]').forEach(el => {
      const title = el.querySelector('.predictive-search-results__collection-title');
      const count = el.querySelector('.predictive-search-results__collection-count');
      
      data.collections.push({
        url: el.href,
        title: title?.textContent || '',
        count: count?.textContent || ''
      });
    });

    // Parse pages
    container.querySelectorAll('[data-page-result]').forEach(el => {
      const title = el.querySelector('.predictive-search-results__page-title');
      
      data.pages.push({
        url: el.href,
        title: title?.textContent || ''
      });
    });

    return data;
  }

  renderResults(data, query) {
    this.hideLoadingState();

    const hasProducts = data.products.length > 0;
    const hasCollections = data.collections.length > 0;
    const hasPages = data.pages.length > 0;
    const hasResults = hasProducts || hasCollections || hasPages;

    if (!hasResults) {
      this.showNoResultsState();
      return;
    }

    // Render products (limit to 8 max: 4 columns x 2 rows) - using collection card structure
    if (this.productsGrid) {
      const productsToShow = data.products.slice(0, 8);
      this.productsGrid.innerHTML = productsToShow.map(product => `
        <div class="card-product__wrapper">
          <div class="card__image">
            <a href="${product.url}" class="card-product__media placeholder">
              ${product.image ? `
                <img
                  src="${product.image.src}"
                  srcset="${product.image.srcset}"
                  alt="${this.escapeHtml(product.image.alt)}"
                  class="motion-reduce"
                  loading="lazy"
                />
              ` : ''}
            </a>
          </div>
          <div class="card__content">
            <h3 class="card__heading h5">
              <a href="${product.url}" title="${this.escapeHtml(product.title)}">${this.escapeHtml(product.title)}</a>
            </h3>
            ${product.variantLabel ? `<div class="card__variant">${this.escapeHtml(product.variantLabel)}</div>` : ''}
            <div class="price${product.onSale && product.comparePrice && product.comparePrice !== product.price ? ' price--on-sale' : ''}">
              <div class="price__container">
                ${product.onSale && product.comparePrice && product.comparePrice !== product.price ? `
                  <div class="price__sale">
                    <span>
                      <s class="price-item price-item--regular">${product.comparePrice}</s>
                    </span>
                    <span class="visually-hidden visually-hidden--inline">Sale price</span>
                    <span class="price-item price-item--sale">${product.price}</span>
                  </div>
                ` : `
                  <div class="price__regular">
                    <span class="visually-hidden visually-hidden--inline">Regular price</span>
                    <span class="price-item price-item--regular">${product.price}</span>
                  </div>
                `}
              </div>
            </div>
          </div>
        </div>
      `).join('');
    }

    // Render collections
    if (this.collectionsList && this.collectionsSection) {
      if (hasCollections) {
        this.collectionsSection.hidden = false;
        this.collectionsList.innerHTML = data.collections.map(collection => `
          <li>
            <a href="${collection.url}" class="predictive-search__collection-link">
              ${this.escapeHtml(collection.title)}
              ${collection.count ? `<span class="predictive-search__collection-count">${collection.count}</span>` : ''}
            </a>
          </li>
        `).join('');
      } else {
        this.collectionsSection.hidden = true;
      }
    }

    // Render pages
    if (this.pagesList && this.pagesSection) {
      if (hasPages) {
        this.pagesSection.hidden = false;
        this.pagesList.innerHTML = data.pages.map(page => `
          <li>
            <a href="${page.url}" class="predictive-search__page-link">
              ${this.escapeHtml(page.title)}
            </a>
          </li>
        `).join('');
      } else {
        this.pagesSection.hidden = true;
      }
    }

    // Update "View all results" button
    if (this.viewAllSection && this.viewAllLink && this.viewAllKeyword) {
      this.viewAllSection.hidden = false;
      this.viewAllLink.href = `/search?q=${encodeURIComponent(query)}`;
      this.viewAllKeyword.textContent = query;
    }

    this.showKeywordState();
  }

  // ==================
  // Cache Management
  // ==================

  getFromCache(query) {
    const key = query.toLowerCase();
    if (this.cache.has(key)) {
      const { data, timestamp } = this.cache.get(key);
      // Cache valid for 5 minutes
      if (Date.now() - timestamp < 5 * 60 * 1000) {
        return data;
      }
      // Expired, remove from cache
      this.cache.delete(key);
    }
    return null;
  }

  addToCache(query, data) {
    const key = query.toLowerCase();
    
    // Enforce max cache size
    if (this.cache.size >= this.cacheMaxSize) {
      // Remove oldest entry
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }

    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  // ==================
  // Utilities
  // ==================

  cancelPendingRequests() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  // Use global debounce function from global.js (bound to this context)
  debounce(fn, delay) {
    return debounce(fn.bind(this), delay);
  }

  // Use global escapeHtml function from global.js
  escapeHtml(text) {
    return escapeHtml(text);
  }
}

customElements.define('predictive-search', PredictiveSearch);
